import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { FloatingLabelInput } from '../../components/FloatingLabelInput';
import { sanitizeName } from '../../../lib/validators';
import { LocationAutocomplete } from '../../components/LocationAutocomplete';
import { PrimaryButton } from '../../components/PrimaryButton';
import { getMe, updateMe, uploadFile } from '../../../lib/professional-api';
import { patchUser } from '../../../lib/auth';
import { apiErrorMessage } from '../../../lib/api';
import { RequirePro } from '../../../lib/use-auth';
import { toast } from '../../../lib/toast';

const GENDER_OPTIONS = ['', 'FEMALE', 'MALE', 'PREFER_NOT_TO_SAY'];
const GENDER_LABEL: Record<string, string> = {
  '': '',
  FEMALE: 'Female',
  MALE: 'Male',
  PREFER_NOT_TO_SAY: 'Prefer not to say',
};

export default function ProAccountInfoPage() {
  return (
    <RequirePro>
      <AccountInfoInner />
    </RequirePro>
  );
}

function AccountInfoInner() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    gender: '' as '' | 'FEMALE' | 'MALE' | 'PREFER_NOT_TO_SAY',
    mobile_number: '',
    dial_code: '+44',
    address_line1: '',
    city: '',
    postcode: '',
    country_code: '',
    profile_pic: '',
    // Server-supplied short-lived signed URL we render in <img>. The
    // canonical `profile_pic` is what we send back on save.
    profile_pic_preview: '',
    // Populated by the Google Places picker when address_line1 is
    // chosen. Save requires both to be present so the booking radius
    // search can include this pro.
    latitude: null as number | null,
    longitude: null as number | null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMe();
      const u = res.info?.user ?? {};
      const p = res.info ?? {};
      setForm({
        first_name: u.first_name ?? '',
        last_name: u.last_name ?? '',
        email: u.email ?? '',
        gender: (p.gender ?? '') as any,
        mobile_number: u.mobile_number ?? '',
        dial_code: u.dial_code ?? '+44',
        address_line1: p.address_line1 ?? '',
        city: p.city ?? '',
        postcode: p.postcode ?? '',
        country_code: p.country_code ?? '',
        // Canonical S3 URL we round-trip on save, plus the signed
        // preview URL the backend mints for private-bucket display.
        profile_pic: u.profile_pic ?? '',
        profile_pic_preview:
          u.profile_pic_preview_url ?? u.profile_pic ?? '',
        latitude:
          p.latitude !== null && p.latitude !== undefined
            ? Number(p.latitude)
            : null,
        longitude:
          p.longitude !== null && p.longitude !== undefined
            ? Number(p.longitude)
            : null,
      });
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not load your profile.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    // Address line 1 only counts as captured if it came from the
    // Google Places picker (lat/lng populated). Without lat/lng the
    // booking radius search can't include this pro, so block save
    // here and ask them to re-pick.
    if (
      form.address_line1.trim() &&
      (form.latitude == null || form.longitude == null)
    ) {
      toast.error(
        'Pick your address from the Google suggestions so customers can find you nearby.',
      );
      setEditingField('address_line1');
      return;
    }
    setSaving(true);
    try {
      await updateMe({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        mobile_number: form.mobile_number.trim() || null,
        dial_code: form.dial_code.trim() || null,
        gender: form.gender || null,
        address_line1: form.address_line1.trim() || null,
        city: form.city.trim() || null,
        postcode: form.postcode.trim() || null,
        country_code: form.country_code.trim() || null,
        latitude: form.latitude,
        longitude: form.longitude,
      });
      // Mirror the locally-cached user so the header / sidebar updates
      // without a re-fetch.
      patchUser({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      });
      setEditingField(null);
      toast.success('Profile updated.');
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not save your profile.');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleEdit = (field: string) =>
    setEditingField((prev) => (prev === field ? null : field));
  const isEditing = (field: string) => editingField === field;

  // Strip AWS presigned query params so we never round-trip a signed
  // URL back to the DB (same belt-and-suspenders pattern the wizard
  // uses).
  const stripS3QueryString = (url: string): string => {
    const q = url.indexOf('?');
    return q >= 0 ? url.slice(0, q) : url;
  };

  // Upload the picked file to S3, save the resulting URL to the
  // profile, and refresh the local cache so the header avatar updates
  // immediately. Errors surface as toasts — no inline state.
  const handleAvatarPick = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const filesArr = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (filesArr.length === 0) return;
    const file = filesArr[0];
    // Show the local blob immediately so the avatar reflects the new
    // pick before S3 returns.
    const localBlob = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, profile_pic_preview: localBlob }));
    setUploadingAvatar(true);
    try {
      const res = await uploadFile(file, 'ADMIN_AVATAR');
      const canonical = stripS3QueryString(res.url);
      // Persist immediately so a refresh doesn't lose the avatar.
      await updateMe({ profile_pic: canonical });
      setForm((prev) => ({
        ...prev,
        profile_pic: canonical,
        // Keep the blob preview around until the next page reload —
        // it works regardless of bucket privacy.
      }));
      patchUser({ profile_pic: canonical });
      toast.success('Profile photo updated.');
    } catch (err) {
      // Revert preview on failure.
      URL.revokeObjectURL(localBlob);
      setForm((prev) => ({
        ...prev,
        profile_pic_preview: prev.profile_pic_preview,
      }));
      toast.error(apiErrorMessage(err, 'Could not upload your photo.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (uploadingAvatar) return;
    setUploadingAvatar(true);
    try {
      await updateMe({ profile_pic: null });
      setForm((prev) => ({ ...prev, profile_pic: '', profile_pic_preview: '' }));
      patchUser({ profile_pic: null as any });
      toast.success('Profile photo removed.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove your photo.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const initials = (() => {
    const f = form.first_name?.trim()?.[0] ?? '';
    const l = form.last_name?.trim()?.[0] ?? '';
    const ini = (f + l).toUpperCase();
    return ini || form.email?.[0]?.toUpperCase() || '?';
  })();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        <div className="flex items-start gap-4 mb-12">
          <Link
            to="/professional/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Account information
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Profile, contact details, and address
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#908e8e] text-[14px] py-12">
            Loading your profile…
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row justify-between gap-12">
            <div className="flex-1 max-w-[553px]">
              <div className="space-y-8">
                {/* Profile photo — circular avatar with a Change /
                    Remove menu. Click the avatar (or Change) to pick a
                    file; the upload persists immediately so refresh
                    keeps it. */}
                <div className="flex items-center gap-5">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarPick}
                  />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="relative w-[88px] h-[88px] rounded-full bg-[#3a3335]/[0.04] flex items-center justify-center overflow-hidden border-2 border-[#eee] hover:border-[#8e2a2f] transition-colors disabled:opacity-60"
                    title="Change profile photo"
                    aria-label="Change profile photo"
                  >
                    {form.profile_pic_preview ? (
                      <img
                        src={form.profile_pic_preview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px]">
                        {initials}
                      </span>
                    )}
                    {uploadingAvatar ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                        <span className="material-icons animate-spin text-[20px]">
                          progress_activity
                        </span>
                      </div>
                    ) : null}
                  </button>

                  <div className="flex flex-col gap-1">
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
                      Profile photo
                    </p>
                    <p className="text-[#908e8e] text-[12px] mb-1">
                      JPG, PNG or WebP — up to 3 MB.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        className="h-[34px] px-4 rounded-[8px] bg-[#8e2a2f] text-white text-[12px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
                      >
                        {uploadingAvatar
                          ? 'Uploading…'
                          : form.profile_pic
                            ? 'Change'
                            : 'Upload'}
                      </button>
                      {form.profile_pic ? (
                        <button
                          type="button"
                          onClick={handleAvatarRemove}
                          disabled={uploadingAvatar}
                          className="h-[34px] px-4 rounded-[8px] border border-[#eee] text-[#908e8e] text-[12px] font-bold hover:text-[#8e2a2f] hover:border-[#8e2a2f] transition-colors disabled:opacity-60"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FloatingLabelInput
                    label="First name *"
                    value={form.first_name}
                    onChange={(v) =>
                      setForm({ ...form, first_name: sanitizeName(v) })
                    }
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('first_name')}
                    rightAction={{
                      icon: isEditing('first_name') ? 'check' : 'edit',
                      label: isEditing('first_name') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('first_name'),
                    }}
                  />
                  <FloatingLabelInput
                    label="Last name *"
                    value={form.last_name}
                    onChange={(v) =>
                      setForm({ ...form, last_name: sanitizeName(v) })
                    }
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('last_name')}
                    rightAction={{
                      icon: isEditing('last_name') ? 'check' : 'edit',
                      label: isEditing('last_name') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('last_name'),
                    }}
                  />
                </div>

                {/* Gender — themed select with custom chevron + an
                    Edit/Done control that matches the pencil-icon style
                    of the FloatingLabelInput rows. */}
                <div>
                  <label className="text-xs font-bold text-[#908e8e] block mb-1">
                    Gender
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <select
                        value={form.gender}
                        onChange={(e) =>
                          setForm({ ...form, gender: e.target.value as any })
                        }
                        disabled={!isEditing('gender')}
                        className="h-[50px] w-full rounded-[8px] border border-[#eee] bg-white pl-4 pr-10 text-[14px] font-bold text-[#3a3335] outline-none appearance-none cursor-pointer focus:border-[#8e2a2f] focus:ring-1 focus:ring-[#8e2a2f] disabled:bg-[#fafafa] disabled:text-[#908e8e] disabled:cursor-not-allowed"
                      >
                        {GENDER_OPTIONS.map((g) => (
                          <option key={g} value={g}>
                            {GENDER_LABEL[g] || '— Select —'}
                          </option>
                        ))}
                      </select>
                      <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-[#908e8e] pointer-events-none text-[20px]">
                        keyboard_arrow_down
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleEdit('gender')}
                      className="flex items-center gap-2 text-[#908e8e] hover:text-[#3a3335] transition-colors font-['DM_Sans',sans-serif] text-[14px] shrink-0"
                    >
                      <span className="material-icons !text-[18px]">
                        {isEditing('gender') ? 'check' : 'edit'}
                      </span>
                      <span className="font-medium">
                        {isEditing('gender') ? 'Done' : 'Edit'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Date of birth removed 2026-06-04 — Stripe Connect
                    Express collects + verifies DOB on its hosted
                    onboarding page; we no longer keep a local copy. */}

                <FloatingLabelInput
                  label="Email address"
                  type="email"
                  value={form.email}
                  onChange={() => undefined}
                  containerHeight="h-[60px]"
                  bottomOnly={true}
                  disabled={true}
                />

                <div className="grid grid-cols-[100px_1fr] gap-4">
                  <FloatingLabelInput
                    label="Dial code"
                    value={form.dial_code}
                    onChange={(v) => setForm({ ...form, dial_code: v })}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('mobile_number')}
                  />
                  <FloatingLabelInput
                    label="Mobile number"
                    value={form.mobile_number}
                    onChange={(v) => setForm({ ...form, mobile_number: v })}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('mobile_number')}
                    rightAction={{
                      icon: isEditing('mobile_number') ? 'check' : 'edit',
                      label: isEditing('mobile_number') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('mobile_number'),
                    }}
                  />
                </div>

                {isEditing('address_line1') ? (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <LocationAutocomplete
                        label="Address"
                        value={form.address_line1}
                        // Free-typing clears lat/lng so save validation
                        // forces a re-pick from Google. Without lat/lng
                        // we can't include this pro in radius search.
                        onChange={(v) =>
                          setForm({
                            ...form,
                            address_line1: v,
                            latitude: null,
                            longitude: null,
                          })
                        }
                        onPlaceSelected={(place) =>
                          setForm({
                            ...form,
                            address_line1: place.description,
                            city: place.city ?? form.city,
                            postcode: place.postcode ?? form.postcode,
                            country_code:
                              place.country_code?.toUpperCase() ??
                              form.country_code,
                            latitude: place.latitude ?? null,
                            longitude: place.longitude ?? null,
                          })
                        }
                        containerHeight="h-[60px]"
                        icon="location_on"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleEdit('address_line1')}
                      className="h-[60px] px-4 rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[12px] hover:bg-[#8e2a2f]/[0.04]"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <FloatingLabelInput
                    label="Address"
                    value={form.address_line1}
                    onChange={() => undefined}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={true}
                    rightAction={{
                      icon: 'edit',
                      label: 'Edit',
                      onClick: () => toggleEdit('address_line1'),
                    }}
                  />
                )}

                <div className="grid grid-cols-2 gap-4">
                  <FloatingLabelInput
                    label="City"
                    value={form.city}
                    onChange={(v) => setForm({ ...form, city: v })}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('city')}
                    rightAction={{
                      icon: isEditing('city') ? 'check' : 'edit',
                      label: isEditing('city') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('city'),
                    }}
                  />
                  <FloatingLabelInput
                    label="Postcode"
                    value={form.postcode}
                    onChange={(v) => setForm({ ...form, postcode: v })}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('postcode')}
                    rightAction={{
                      icon: isEditing('postcode') ? 'check' : 'edit',
                      label: isEditing('postcode') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('postcode'),
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[361px] shrink-0">
              <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
                <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-2">
                  General information
                </h3>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
                  Keep your details up to date so customers can find you and we
                  can reach you about bookings. Your email is used for sign-in
                  and can't be changed here.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 flex items-center gap-4">
          <PrimaryButton
            className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Update Settings'}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => {
              setEditingField(null);
              load();
            }}
            className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
          >
            Cancel
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
